import { IconButton } from '@creditornot/cb-button';
import { CheckmarkCircleFilled, CrossCircleFilled, PlusCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, elevationCss, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import styled, { css } from 'styled-components';

type TableRowProps = {
  $hasError?: boolean;
  $isExpanded?: boolean;
};

export const Root = styled.div`
  ${elevationCss(2)}
  padding: 48px;
  border-radius: 8px;
  width: 100%;
`;

export const TableHorizontalScrollContainer = styled.div`
  overflow-x: auto;
`;

export const Table = styled.div`
  display: table;
  border-collapse: collapse;
  width: 100%;
`;

export const TableRow = styled.div<TableRowProps>`
  ${({ $hasError, $isExpanded }) => css`
    display: table-row;
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
    color: ${cbSemanticCssVars.textPrimary};
    cursor: ${$hasError ? 'pointer' : 'default'};
    /* ignore-color */
    background-color: ${$isExpanded ? colors.pepper4 : undefined};
    &:hover {
      background-color: ${cssThemedValue({ light: colors.wolt8, dark: colors.salt12 })};
    }
  `}
`;

export const ErrorRow = styled(TableRow)`
  /* ignore-color */
  background-color: ${colors.pepper4};
`;

export const TableCell = styled.div<{ $fixedWidth?: boolean }>`
  ${typographyCss.Body3()}
  padding: 16px;
  display: table-cell;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
  width: ${({ $fixedWidth }) => ($fixedWidth ? '50px' : 'unset')};
`;

export const TableHeader = styled(TableRow)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  cursor: default;

  &:hover {
    background-color: unset;
  }

  ${TableCell} {
    font-weight: 700;
  }
`;

export const FullWidthCell = styled.td.attrs({ colSpan: 100 })``;

export const ErrorMessageCell = styled(FullWidthCell)`
  ${typographyCss.Body3()};
  padding: 16px;
  white-space: pre-wrap;
`;

export const ErrorIcon = styled(CrossCircleFilled).attrs({
  width: 16,
  height: 16,
})`
  display: flex;
  fill: ${colors.strawberry100};
`;

export const UpdateIcon = styled(CheckmarkCircleFilled).attrs({
  width: 16,
  height: 16,
})`
  display: flex;
  fill: ${colors.wolt100};
`;

export const AdditionIcon = styled(PlusCircleFilled).attrs({
  width: 16,
  height: 16,
})`
  display: flex;
  fill: ${colors.lime100};
`;

type ArrowIconButtonProps = {
  $isExpanded: boolean;
};

export const ArrowIconContainer = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'grey',
  background: 'staticWhite',
  alignment: 'boundingBox',
})<ArrowIconButtonProps>`
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
`;

export const Illustration = styled.div`
  width: 100%;
  max-width: 375px;
  margin-block-end: 24px;
`;

export const EmptyText = styled.span`
  ${typographyCss.Body3()}
`;

export const EmptyResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 64px;
`;
